import { createId } from '@paralleldrive/cuid2';
import { timestamp } from './utils/Time';
import { View } from './View';
import { StudyEventContext } from './type';

export type DefaultSpanData = StudyEventContext & {
  type: 'STUDY_TIME_SPENT';
};

export type EndReason = 'FINISH' | 'PAGE_CHANGE';

export interface SpanContext<SpanData = DefaultSpanData> {
  parentSpanId?: string;
  userId?: string;
  activitySessionId?: string;
  data: SpanData;
  view?: View;
  startTime?: number;
}

export interface SpanOptions<SpanData extends DefaultSpanData = DefaultSpanData>
  extends SpanContext<SpanData> {
  onSpanEnd?: (span: Span<SpanData>) => void;
  onSpanRemove?: (span: Span<SpanData>) => void;
}

export class Span<SpanData extends DefaultSpanData = DefaultSpanData> {
  readonly startTime: number;
  readonly spanId: string;
  readonly parentSpanId?: string;
  readonly userId?: string;
  readonly activitySessionId?: string;
  endTime?: number;
  duration?: number;
  data: SpanData;
  onSpanEnd?: (span: Span<SpanData>) => void;
  onSpanRemove?: (span: Span<SpanData>) => void;
  view?: View;
  reason?: EndReason;

  constructor(option: SpanOptions<SpanData>) {
    this.startTime = option?.startTime ?? timestamp();
    this.spanId = createId();
    this.parentSpanId = option?.parentSpanId;
    this.activitySessionId = option?.activitySessionId;
    this.onSpanEnd = option?.onSpanEnd;
    this.onSpanRemove = option?.onSpanRemove;
    this.view = option?.view;
    this.userId = option?.userId;

    this.data = option.data;
  }

  isRecording() {
    return !this.endTime;
  }

  end(endTime?: number, reason: EndReason = 'FINISH') {
    if (this.endTime) {
      return;
    }
    this.endTime = endTime ?? timestamp();
    this.duration = this.endTime - this.startTime;
    this.reason = reason;
    this.onSpanEnd?.(this);
  }

  startChild(option: SpanOptions<SpanData>) {
    return new Span<SpanData>({
      ...option,
      parentSpanId: this.spanId,
      onSpanEnd: this?.onSpanEnd,
    });
  }

  remove() {
    this.onSpanRemove?.(this);
  }
}
